import { useEffect, useState } from 'react';
import { DEFAULT_SITE_CONTENT } from './defaultSiteContent';
import { parseSiteContentPayload } from './parseSiteContent';
import type { I3SiteContentV1 } from './types';
import { BRIDGE_ENABLE_CMS_FETCH } from '../../../siteConfig';
import { getPublicPortalSiteContentUrl, getStaticBridgeCmsUrl } from './bridgeCmsEndpoints';

export type UseI3SiteContentResult = {
  content: I3SiteContentV1;
  loading: boolean;
  error: Error | null;
  /** true after fetch attempt finished (success or failure) */
  ready: boolean;
  /** When set, public pages loaded CMS from the API (SQL) */
  source: 'sql-api' | 'static' | null;
};

/** Set `REACT_APP_BRIDGE_ENABLE_CMS_FETCH=true` to load CMS JSON or the portal API. Default: embedded defaults only. */
const ENABLE_CMS_FETCH = BRIDGE_ENABLE_CMS_FETCH;

/**
 * Site content: defaults to embedded `DEFAULT_SITE_CONTENT` (no network).
 * Opt-in to remote/static JSON with `REACT_APP_BRIDGE_ENABLE_CMS_FETCH=true`.
 */
export function useI3SiteContent(): UseI3SiteContentResult {
  const [content, setContent] = useState<I3SiteContentV1>(() => DEFAULT_SITE_CONTENT);
  const [loading, setLoading] = useState(ENABLE_CMS_FETCH);
  const [error, setError] = useState<Error | null>(null);
  const [ready, setReady] = useState(!ENABLE_CMS_FETCH);
  const [source, setSource] = useState<'sql-api' | 'static' | null>(null);

  useEffect(() => {
    if (!ENABLE_CMS_FETCH) {
      return;
    }

    const staticUrl = getStaticBridgeCmsUrl();
    const publicApiUrl = getPublicPortalSiteContentUrl();
    let cancelled = false;

    (async () => {
      try {
        if (publicApiUrl) {
          try {
            const res = await fetch(publicApiUrl, { cache: 'no-store' });
            if (res.ok) {
              const body = (await res.json()) as { content?: unknown };
              if (body?.content !== undefined && body.content !== null) {
                if (cancelled) return;
                setContent(parseSiteContentPayload(body.content));
                setError(null);
                setSource('sql-api');
                return;
              }
            }
          } catch {
            /* fall through to static */
          }
        }

        const res = await fetch(staticUrl, { cache: 'no-store' });
        if (!res.ok) throw new Error(`CMS fetch ${res.status}`);
        const json: unknown = await res.json();
        if (cancelled) return;
        setContent(parseSiteContentPayload(json));
        setError(null);
        setSource('static');
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setContent(DEFAULT_SITE_CONTENT);
        setSource(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { content, loading, error, ready, source };
}
